import * as yup from 'yup';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { memo, useCallback } from 'react';
import {
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography,
  useEventCallback,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import PmiTextField from 'shell/components/shared/text-field';
import TypeRightsList from '../shared/type-rights-list';
import ReleaseModeList from '../shared/release-mode-list';
import HolderTypeList from '../shared/holder-type-list';
import GroupTypeDocumentList from '../shared/group-type-document-list';

export interface TypeDocForm {
  typeDoc?: string;
  code?: string;
  typeRights?: string;
  description?: string;
  mode?: string;
  groupDoc?: string;
  isUse?: boolean;
  isAire?: boolean;
  template?: string;
  holderType?: string;
}

interface FormProps {
  onOk: (FormComponent?: TypeDocForm) => void;
  initialValues?: TypeDocForm;
  onCancel: () => void;
  disabled?: boolean;
  isEdit?: boolean;
  title?: string;
}
const validationSchema = yup.object({
  typeDoc: yup.string().required('Il campo è obbligatorio'),
  code: yup.string().required('Il campo è obbligatorio'),
});

const FormComponent = ({
  onOk,
  initialValues,
  onCancel,
  disabled,
  isEdit,
  title,
}: FormProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const typeDocForm = useFormik({
    initialValues: initialValues || {
      typeDoc: '',
      code: '',
      typeRights: '',
      description: '',
      mode: '',
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const onChangeTypeDocumentList = useEventCallback((event: SelectChangeEvent<string>) => {
    typeDocForm.setFieldValue('typeDoc', event.target.value);
  });

  const onChangeTypeRightsList = useEventCallback((event: SelectChangeEvent<string>) => {
    typeDocForm.setFieldValue('typeRights', event.target.value);
  });

  const onChangeReleaseModeList = useEventCallback((event: SelectChangeEvent<string>) => {
    typeDocForm.setFieldValue('mode', event.target.value);
  });

  const onChangeHolderTypeList = useEventCallback((event: SelectChangeEvent<string>) => {
    typeDocForm.setFieldValue('holderType', event.target.value);
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={typeDocForm.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <GroupTypeDocumentList
                  required
                  onChange={onChangeTypeDocumentList}
                  value={typeDocForm.values.typeDoc || ''}
                  error={typeDocForm.touched.typeDoc && Boolean(typeDocForm.errors.typeDoc)}
                  disabled={disabled || isEdit}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Codice tipo documento"
                  label="Codice tipo documento *"
                  id="code"
                  name="code"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={typeDocForm.values.code || ''}
                  onChange={typeDocForm.handleChange}
                  inputProps={{
                    maxLength: '2',
                  }}
                  helperText={typeDocForm.errors.code}
                  error={typeDocForm.touched.code && Boolean(typeDocForm.errors.code)}
                  disabled={disabled || isEdit}
                />
              </Grid>
              <Grid item xs={12} md={10}>
                <PmiTextField
                  aria-label="Descrizione tipo documento"
                  label="Descrizione tipo documento"
                  id="description"
                  name="description"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={typeDocForm.values.description || ''}
                  onChange={typeDocForm.handleChange}
                  inputProps={{
                    maxLength: '50',
                  }}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TypeRightsList
                  onChange={onChangeTypeRightsList}
                  value={typeDocForm.values.typeRights || ''}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <ReleaseModeList
                  onChange={onChangeReleaseModeList}
                  value={typeDocForm.values.mode || ''}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={10}>
                <PmiTextField
                  aria-label="Template"
                  label="Template"
                  id="template"
                  name="template"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={typeDocForm.values.template || ''}
                  onChange={typeDocForm.handleChange}
                  inputProps={{
                    maxLength: '50',
                  }}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Gruppo documento"
                  label="Gruppo documento"
                  id="groupDoc"
                  name="groupDoc"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={typeDocForm.values.groupDoc || ''}
                  onChange={typeDocForm.handleChange}
                  inputProps={{
                    maxLength: '1',
                  }}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="isUse"
                      checked={typeDocForm.values.isUse || false}
                      onChange={() => typeDocForm.setFieldValue('isUse', !typeDocForm.values.isUse)}
                      inputProps={{ 'aria-label': 'Documento in uso' }}
                      disabled={disabled}
                    />
                  }
                  label="Documento in uso"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <HolderTypeList
                  onChange={onChangeHolderTypeList}
                  value={typeDocForm.values.holderType || ''}
                  disabled={disabled}
                />
                {/* <FormControlLabel
                  control={
                    <Checkbox
                      id="isAire"
                      checked={typeDocForm.values.isAire || false}
                      onChange={() =>
                        typeDocForm.setFieldValue('isAire', !typeDocForm.values.isAire)
                      }
                      inputProps={{ 'aria-label': 'Documento valido per iscritti AIRE' }}
                      disabled={disabled}
                    />
                  }
                  label="Documento valido per iscritti AIRE"
                /> */}
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel();
            }}
          >
            Annulla
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};
FormComponent.displayName = 'type document form component';

const Form = memo(FormComponent);

export default Form;
